import * as XLSX from 'xlsx';
import { supabase } from './supabase';
import { PROMOCODE_CONFIG } from '../config/promocode.js';
import PromocodeDecoder from './promocodeDecoder.js';

class ExcelPromocodeUploader {
  constructor() {
    this.supabase = supabase;
  }

  // Parse Excel file and extract promocodes
  async parseExcelFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first worksheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  // Validate Excel data format
  validateExcelData(data) {
    if (!Array.isArray(data) || data.length === 0) {
      return { valid: false, error: 'No data found in Excel file' };
    }

    const firstRow = data[0];
    
    // Check if it's the new format (with Platform and Plan Type)
    const hasNewFormat = 'Platform' in firstRow && 'Plan Type' in firstRow;
    
    if (hasNewFormat) {
      // New format validation
      const requiredColumns = ['Promocode', 'Platform', 'Plan Type', 'Start Date', 'End Date', 'Credits'];
      for (const column of requiredColumns) {
        if (!(column in firstRow)) {
          return { valid: false, error: `Missing required column: ${column}` };
        }
      }
      
      // Validate structured promocode format
      for (const row of data) {
        if (!row.Promocode || typeof row.Promocode !== 'string') {
          return { valid: false, error: 'Invalid promocode format in Excel data' };
        }
        
        // Check if it's a structured promocode
        const structuredMatch = row.Promocode.match(/^([A-Z]{2})([LMY])(\d+[KM]?)(\d{4})([A-Z0-9]{8,12})$/);
        if (!structuredMatch) {
          return { valid: false, error: `Promocode "${row.Promocode}" is not in structured format. Expected: [Platform][Plan][Credits][Year][Random]` };
        }
        
        // Validate platform exists
        if (!row.Platform || typeof row.Platform !== 'string') {
          return { valid: false, error: 'Platform must be specified for structured promocodes' };
        }
        
        // Validate plan type
        const validPlanTypes = ['lifetime', 'monthly', 'yearly'];
        if (!validPlanTypes.includes(row['Plan Type'])) {
          return { valid: false, error: `Invalid plan type: ${row['Plan Type']}. Must be one of: ${validPlanTypes.join(', ')}` };
        }
      }
    } else {
      // Legacy format validation (backward compatibility)
      const requiredColumns = ['Promocode', 'Start Date', 'End Date', 'Credits'];
      for (const column of requiredColumns) {
        if (!(column in firstRow)) {
          return { valid: false, error: `Missing required column: ${column}` };
        }
      }
      
      // Basic validation for legacy format
      for (const row of data) {
        if (!row.Promocode || typeof row.Promocode !== 'string') {
          return { valid: false, error: 'Invalid promocode format in Excel data' };
        }
      }
    }

    return { valid: true, format: hasNewFormat ? 'structured' : 'legacy' };
  }

  // Upload promocodes to database
  async uploadPromocodesToDatabase(promocodesData) {
    try {
      // Validate data first to determine format
      const validation = this.validateExcelData(promocodesData);
      if (!validation.valid) {
        throw new Error(validation.error);
      }
      
      const promocodes = promocodesData.map((row, index) => {
        try {
          // Only include columns that exist in the database table
          return {
            code: row.Promocode,
            start_date: this.parseDate(row['Start Date']),
            end_date: this.parseDate(row['End Date']),
            credits_amount: parseInt(row.Credits) || 140000,
            created_at: new Date().toISOString()
          };
        } catch (error) {
          console.error(`Error processing row ${index + 1}:`, error.message);
          console.error('Row data:', row);
          throw new Error(`Row ${index + 1}: ${error.message}`);
        }
      });

      const { data, error } = await this.supabase
        .from(PROMOCODE_CONFIG.tableName)
        .insert(promocodes)
        .select();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error uploading promocodes:', error);
      return { success: false, error: error.message };
    }
  }

  // Format date to YYYY-MM-DD without timezone conversion
  formatDateToISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Parse date from Excel (handles various formats)
  parseDate(dateValue) {
    if (!dateValue) return null;
    
    console.log('Parsing date:', dateValue, 'Type:', typeof dateValue);
    
    // Handle Excel serial date numbers
    if (typeof dateValue === 'number') {
      // Excel date serial number (days since 1900-01-01)
      const excelEpoch = new Date(1900, 0, 1);
      const date = new Date(excelEpoch.getTime() + (dateValue - 2) * 24 * 60 * 60 * 1000);
      const result = this.formatDateToISO(date);
      console.log('Excel serial date converted:', result);
      return result;
    }
    
    // Handle string dates
    if (typeof dateValue === 'string') {
      const trimmedValue = dateValue.trim();
      
      // Try comprehensive date format parsing
      const formats = [
        // ISO formats
        { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, handler: (match) => [match[1], match[2], match[3]] },
        
        // US formats (MM/DD/YYYY)
        { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, handler: (match) => [match[3], match[1], match[2]] },
        
        // European formats (DD/MM/YYYY)
        { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, handler: (match) => [match[3], match[2], match[1]] },
        
        // Dot separators (DD.MM.YYYY or MM.DD.YYYY)
        { pattern: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, handler: (match) => [match[3], match[2], match[1]] },
        
        // Dash separators (DD-MM-YYYY or MM-DD-YYYY)
        { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, handler: (match) => [match[3], match[2], match[1]] },
        
        // Space separators (DD MM YYYY)
        { pattern: /^(\d{1,2})\s+(\d{1,2})\s+(\d{4})$/, handler: (match) => [match[3], match[2], match[1]] },
        
        // Text month formats
        { pattern: /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})$/, 
          handler: (match) => {
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'];
            const month = (monthNames.indexOf(match[1]) + 1).toString().padStart(2, '0');
            return [match[3], month, match[2]];
          }
        },
        
        // Abbreviated month formats
        { pattern: /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s+(\d{4})$/, 
          handler: (match) => {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = (monthNames.indexOf(match[1]) + 1).toString().padStart(2, '0');
            return [match[3], month, match[2]];
          }
        },
        
        // DD Month YYYY format
        { pattern: /^(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/, 
          handler: (match) => {
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'];
            const month = (monthNames.indexOf(match[2]) + 1).toString().padStart(2, '0');
            return [match[3], month, match[1]];
          }
        }
      ];
      
      // Try each format
      for (const format of formats) {
        const match = trimmedValue.match(format.pattern);
        if (match) {
          try {
            const [year, month, day] = format.handler(match);
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            
            // Validate the date
            if (!isNaN(date.getTime()) && 
                date.getFullYear() == year && 
                date.getMonth() == month - 1 && 
                date.getDate() == day) {
              const result = this.formatDateToISO(date);
              console.log(`Date parsed with format ${format.pattern.source}:`, result);
              return result;
            }
          } catch (error) {
            console.log(`Format ${format.pattern.source} failed:`, error.message);
            continue;
          }
        }
      }
      
      // Fallback: try direct Date parsing
      const date = new Date(trimmedValue);
      if (!isNaN(date.getTime())) {
        const result = this.formatDateToISO(date);
        console.log('Direct date parsing fallback:', result);
        return result;
      }
      
      throw new Error(`Invalid date format: ${dateValue}`);
    }
    
    // Handle Date objects
    if (dateValue instanceof Date) {
      const result = this.formatDateToISO(dateValue);
      console.log('Date object converted:', result);
      return result;
    }
    
    throw new Error(`Unsupported date type: ${typeof dateValue}`);
  }

  // Generate Excel template
  generateExcelTemplate() {
    const templateData = [
      {
        'Promocode': 'XYL140K2025ABC12345',
        'Platform': 'Xylium',
        'Plan Type': 'lifetime',
        'Start Date': '2024-01-01',
        'End Date': '2025-01-01',
        'Credits': 140000
      },
      {
        'Promocode': 'GPM10K2025XYZ98765',
        'Platform': 'Generic',
        'Plan Type': 'monthly',
        'Start Date': '2024-06-01',
        'End Date': '2025-06-01',
        'Credits': 10000
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Promocodes');
    
    return XLSX.writeFile(workbook, 'promocodes_template.xlsx');
  }

  // Export promocodes to Excel
  async exportPromocodesToExcel(promocodes) {
    
    const excelData = promocodes.map((promocode) => {
      // Decode the promocode to extract platform and plan type information
      const decoded = PromocodeDecoder.decode(promocode.code);
      
      return {
        'Promocode': promocode.code,
        'Platform': decoded.isValid ? decoded.platform : 'unknown',
        'Plan Type': decoded.isValid ? decoded.planType : 'unknown',
        'Start Date': promocode.start_date,
        'End Date': promocode.end_date,
        'Credits': promocode.credits_amount
      };
    });
    
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Promocodes');
    
    const fileName = `promocodes_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    return fileName;
  }

  // Bulk upload with validation
  async bulkUploadPromocodes(file) {
    try {
      // Parse Excel file
      const excelData = await this.parseExcelFile(file);
      
      // Validate data format
      const validation = this.validateExcelData(excelData);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Validate each promocode format (basic validation for pre-generated codes)
      const invalidCodes = [];
      for (const row of excelData) {
        if (!row.Promocode) {
          invalidCodes.push(row.Promocode);
        }
      }

      if (invalidCodes.length > 0) {
        return { 
          success: false, 
          error: `Invalid promocode format: ${invalidCodes.join(', ')}` 
        };
      }

      // Upload to database
      const uploadResult = await this.uploadPromocodesToDatabase(excelData);
      
      if (uploadResult.success) {
        return {
          success: true,
          message: `Successfully uploaded ${excelData.length} promocodes`,
          data: uploadResult.data
        };
      } else {
        return uploadResult;
      }
    } catch (error) {
      console.error('Error in bulk upload:', error);
      return { success: false, error: error.message };
    }
  }

  // Get upload statistics
  async getUploadStatistics() {
    try {
      const { data, error } = await this.supabase
        .from(PROMOCODE_CONFIG.tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const stats = {
        total: data.length,
        used: data.filter(p => p.is_used).length,
        active: data.filter(p => !p.is_used && new Date(p.end_date) >= new Date()).length,
        expired: data.filter(p => new Date(p.end_date) < new Date()).length
      };

      return { success: true, stats };
    } catch (error) {
      console.error('Error getting upload statistics:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new ExcelPromocodeUploader();
